import React, { useState, useRef, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import toast from 'react-hot-toast';
import { updateProfile } from 'firebase/auth';
import {
  sendOtp, verifyOtp, updateUserProfile, getAuthErrorKey, getAuthErrorDebug,
  setNativeOtpCallbacks, auth, hasPendingOtpSession, getPendingOtpPhone,
  getPendingVerificationId, waitForSignedInUser, prewarmRecaptcha, verifyTruecallerPayload,
  prewarmTruecallerFunction, recordConsent, attestAgeAndConsent,
} from '../services/firebase';
import {
  useApp, markAwaitingName, clearAwaitingName, rememberLocalName, readLocalName,
} from '../context/AppContext';
import { useLanguage } from '../i18n/LanguageContext';
import { APP } from '../config/app';
import { PRIVACY_NOTICE_VERSION, ESSENTIAL_PURPOSES } from '../config/privacy';
import LangToggle from '../components/LangToggle';
import BrandHero from '../components/BrandHero';
import { listenForWebOtp } from '../utils/otpAutofill';
import { shouldUseNativePhoneAuth, isNativeApp } from '../utils/platform';
import { startTruecallerLogin, isTruecallerAvailable, warmupTruecaller } from '../utils/truecaller';
import { runTruecallerSignIn } from '../utils/truecallerSignIn';
import { hapticSelection } from '../utils/haptics';
import { isValidName, cleanName } from '../utils/name';
import LoginOtpInput from '../components/LoginOtpInput';
import PilerTriviaCard from '../components/PilerTriviaCard';
import { openLegalUrl } from '../utils/legal';
import { normalizePhone } from '../utils/phone';

export default function Login({ startAt }) {
  const [step, setStep] = useState(startAt || 'phone');
  const [phone, setPhone] = useState('');
  const [otp, setOtp] = useState('');
  const [name, setName] = useState('');
  const [verificationId, setVerificationId] = useState('');
  const [loading, setLoading] = useState(false);
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOverlay, setVerifyingOverlay] = useState(null);
  const [resendIn, setResendIn] = useState(0);
  const verifyingRef = useRef(false);
  // The running Truecaller sign-in (its timers); cancelled if this screen goes away.
  const tcRunRef = useRef(null);
  const truecallerTriedRef = useRef(false);
  const truecallerBusyRef = useRef(false);
  const navigate = useNavigate();
  const { refreshUserProfile, seedUserProfile, needsName, profileReady, profileLoaded, user } = useApp();
  const { t } = useLanguage();

  useEffect(() => {
    return () => {
      if (tcRunRef.current) tcRunRef.current.cancel();
    };
  }, []);

  useEffect(() => {
    if (resendIn <= 0) return undefined;
    const id = setInterval(() => setResendIn((s) => Math.max(0, s - 1)), 1000);
    return () => clearInterval(id);
  }, [resendIn]);

  useEffect(() => { if (startAt) setStep(startAt); }, [startAt]);
  useEffect(() => {
    if (!shouldUseNativePhoneAuth() || startAt) return;
    const pendingPhone = getPendingOtpPhone();
    if (hasPendingOtpSession() && pendingPhone) {
      const digits = pendingPhone.replace(/\D/g, '').slice(-10);
      if (digits.length === 10) setPhone(digits);
      setVerificationId(getPendingVerificationId());
      setStep('otp');
    }
  }, [startAt]);

  useEffect(() => {
    if (user && profileReady && profileLoaded && needsName) setStep('name');
  }, [user, profileReady, profileLoaded, needsName]);
  useEffect(() => {
    if (user && profileReady && profileLoaded && !needsName) navigate('/', { replace: true });
  }, [user, profileReady, profileLoaded, needsName, navigate]);

  useEffect(() => {
    if (step !== 'name') return;
    const prefill = (isValidName(user?.displayName) ? cleanName(user.displayName) : '') || readLocalName();
    if (prefill) setName((prev) => (prev.trim() ? prev : prefill));
  }, [step, user?.displayName]);

  const finishAfterAuth = useCallback(async (firebaseUser) => {
    const userData = await refreshUserProfile(firebaseUser.uid);
    // Only accept an actual name. A phone number saved as the name on an old
    // build counts as no name → the passenger goes to the name step to fix it.
    const storedName = isValidName(userData?.name) ? cleanName(userData.name) : '';
    const fallback = storedName
      || (isValidName(firebaseUser.displayName) ? cleanName(firebaseUser.displayName) : '')
      || readLocalName();
    if (fallback) {
      if (!storedName) {
        await updateUserProfile(firebaseUser.uid, { name: fallback, profileComplete: true });
        rememberLocalName(fallback);
        await refreshUserProfile(firebaseUser.uid, { name: fallback, profileComplete: true });
      } else {
        rememberLocalName(fallback);
      }
      clearAwaitingName();
      return userData;
    }
    markAwaitingName(firebaseUser.uid);
    setStep('name');
    return userData;
  }, [refreshUserProfile]);

  const finishPrivacyOnLogin = useCallback(async (firebaseUser) => {
    if (!firebaseUser?.uid) return;
    try {
      const purposes = [...ESSENTIAL_PURPOSES];
      await Promise.allSettled([
        attestAgeAndConsent(firebaseUser.uid, PRIVACY_NOTICE_VERSION),
        recordConsent(firebaseUser.uid, {
          purposes,
          noticeVersion: PRIVACY_NOTICE_VERSION,
          method: 'login_otp',
        }),
      ]);
    } catch (e) {
      console.warn('privacy consent record failed', e?.message);
    }
  }, []);

  const goHomeAfterAuth = useCallback((firebaseUser) => {
    navigate('/', { replace: true });
    Promise.allSettled([
      finishPrivacyOnLogin(firebaseUser),
      finishAfterAuth(firebaseUser),
    ]).catch((e) => console.warn('background auth sync:', e));
  }, [navigate, finishAfterAuth, finishPrivacyOnLogin]);

  const handleSendOtp = async () => {
    const normalizedPhone = normalizePhone(phone);
    if (!/^\+91\d{10}$/.test(normalizedPhone)) { toast.error(t('login.invalidPhone')); return; }
    hapticSelection();
    const isNative = shouldUseNativePhoneAuth();
    setSendingOtp(true);
    setLoading(true);
    setVerificationId('');
    if (isNative) {
      toast(t('login.sendingOtpToast'), { id: 'otp-sent', icon: '📱', duration: 8000 });
    }
    try {
      const sent = await sendOtp(normalizedPhone);
      if (sent?.autoVerified) {
        const user = auth.currentUser || await waitForSignedInUser(4000);
        if (user) {
          goHomeAfterAuth(user);
          return;
        }
      }
      if (sent?.web) {
        setOtp('');
        toast.success(t('login.otpSent'));
        setResendIn(30);
        setStep('otp');
        return;
      }
      if (!sent?.verificationId && !hasPendingOtpSession()) {
        toast.error(t('authErrors.noOtpSession'));
        return;
      }
      setVerificationId(sent?.verificationId || getPendingVerificationId());
      setOtp('');
      setResendIn(30);
      setStep('otp');
      toast.success(t('login.otpSent'), { id: 'otp-sent', duration: 4000 });
    } catch (e) {
      console.error('sendOtp failed:', e?.code, e?.message, e);
      const key = getAuthErrorKey(e);
      const hint = isNative ? ` (${getAuthErrorDebug(e)})` : '';
      const msg = `${t(key)}${hint}`;
      // Setup detail (the installed SHA-256) is for the developer, not the rider.
      if (isNative && key === 'authErrors.appNotAuthorized') {
        const sig = typeof window !== 'undefined' ? window.__MT_SIGNING__ : null;
        if (sig?.sha256?.[0]) console.error('Play Integrity needs SHA-256:', sig.sha256[0], sig);
      }
      toast.error(msg, { duration: 12000 });
    } finally {
      setSendingOtp(false);
      setLoading(false);
    }
  };

  const handleTruecallerLogin = useCallback(async (payload) => {
    if (verifyingRef.current) return;
    if (tcRunRef.current) tcRunRef.current.cancel();
    truecallerTriedRef.current = true;
    verifyingRef.current = true;
    setLoading(true);
    const overlayTitle = t('login.signingInTruecaller') || 'Signing in via Truecaller…';
    setVerifyingOverlay({
      title: overlayTitle,
      subtitle: t('login.verifyingProfile') || 'Verifying your profile securely',
    });

    // Timing policy lives in utils/truecallerSignIn.js. A slow sign-in only shows a
    // "still working" hint at 10 s; the rider is sent to OTP only at 30 s, and only
    // if they are not already signed in. A login key that comes back late is still
    // used (below), so a verified rider never pays for an SMS they did not need.
    const run = runTruecallerSignIn({
      verify: () => verifyTruecallerPayload(payload),
      isSignedIn: () => Boolean(auth.currentUser),
      onHint: () => setVerifyingOverlay({
        title: overlayTitle,
        subtitle: t('login.stillSigningIn') || 'Still signing you in — this can take a moment…',
      }),
      onGiveUp: () => {
        verifyingRef.current = false;
        setLoading(false);
        setVerifyingOverlay(null);
        toast.error(t('login.tcTimeoutUseOtp') || 'Truecaller took too long. Please verify via OTP.', { duration: 6000 });
      },
    });
    tcRunRef.current = run;

    const outcome = await run.promise;
    if (outcome.kind === 'success') {
      const result = outcome.result;
      if (result?.user) {
        if (result.profile) {
          seedUserProfile(result.profile);
        }
        goHomeAfterAuth(result.user);
      }
    } else if (!outcome.gaveUp) {
      // After the "use OTP" message the rider was already told; don't report it twice.
      console.error('Truecaller verification error:', outcome.error);
      const detail = outcome.error?.message || outcome.error?.code || '';
      toast.error(detail ? `${t('login.tcFailed')} ${detail}` : t('login.tcFailedUseOtp'), { duration: 8000 });
    }
    if (!outcome.gaveUp) {
      setLoading(false);
      verifyingRef.current = false;
      setVerifyingOverlay(null);
    }
  }, [goHomeAfterAuth, seedUserProfile, t]);

  useEffect(() => {
    if (!shouldUseNativePhoneAuth()) {
      prewarmRecaptcha();
    }
    window.onTruecallerSuccess = (payload) => {
      handleTruecallerLogin(payload);
    };
    window.onTruecallerFailure = (err) => {
      console.warn('Truecaller dismissed or unavailable:', err);
      truecallerTriedRef.current = true;
    };
    return () => {
      delete window.onTruecallerSuccess;
      delete window.onTruecallerFailure;
    };
  }, [handleTruecallerLogin]);

  useEffect(() => {
    if (!isNativeApp()) return undefined;
    warmupTruecaller().catch(() => {});
    prewarmTruecallerFunction();
    return undefined;
  }, []);

  const tryTruecallerLogin = useCallback(async (manual = false) => {
    if (!isNativeApp()) {
      if (manual) toast(t('login.tcAppOnly'), { icon: 'ℹ️' });
      return;
    }
    if (truecallerBusyRef.current) return;
    if (truecallerTriedRef.current && !manual) return;
    if (!manual) truecallerTriedRef.current = true;
    truecallerBusyRef.current = true;
    try {
      prewarmTruecallerFunction();
      await warmupTruecaller();
      const status = await isTruecallerAvailable();
      if (!status.available) {
        if (manual) {
          const msg = status.reason === 'not-usable'
            ? t('login.tcNotUsable')
            : status.reason === 'sdk-not-initialized' || status.reason === 'init-failed'
              ? t('login.tcCantStart')
              : status.reason === 'plugin-error'
                ? t('login.tcPluginMissing')
                : t('login.tcUnavailable');
          toast.error(msg, { id: 'truecaller-error' });
        }
        return;
      }
      await startTruecallerLogin();
    } catch (e) {
      const msg = String(e?.message || '');
      if (manual) {
        toast.error(msg || t('login.tcCantOpen'), { id: 'truecaller-error' });
      } else {
        console.warn('Truecaller auto-popup skipped:', msg || e);
      }
    } finally {
      truecallerBusyRef.current = false;
    }
  }, [t]);

  // Auto-trigger Truecaller 1-tap bottom sheet on native Android app launch
  useEffect(() => {
    if (!isNativeApp() || step !== 'phone' || user) return undefined;
    const timer = setTimeout(() => {
      if (step === 'phone' && !phone && !user && !truecallerTriedRef.current) {
        tryTruecallerLogin(false);
      }
    }, 450);
    return () => clearTimeout(timer);
  }, [step, user, phone, tryTruecallerLogin]);

  const completeOtpLogin = useCallback(async (code) => {
    if (!code || code.length < 6) { toast.error(t('login.invalidOtp')); return; }
    if (verifyingRef.current) return;
    verifyingRef.current = true;
    setLoading(true);
    setVerifyingOverlay({
      title: t('login.verifyingOtp') || 'Verifying OTP…',
      subtitle: t('login.loggingIn') || 'Logging into your account',
    });
    try {
      // If auto-verify already signed in on JS auth, just continue.
      if (auth.currentUser) {
        goHomeAfterAuth(auth.currentUser);
        return;
      }
      const firebaseUser = await verifyOtp(code, verificationId || getPendingVerificationId());
      if (firebaseUser) goHomeAfterAuth(firebaseUser);
    } catch (e) {
      console.error('verifyOtp failed:', e?.code, e?.message, e);
      // Race: auto-verify may have signed in while manual confirm ran.
      if (auth.currentUser) {
        goHomeAfterAuth(auth.currentUser);
        return;
      }
      const key = getAuthErrorKey(e);
      const hint = shouldUseNativePhoneAuth() ? ` (${getAuthErrorDebug(e)})` : '';
      toast.error(`${t(key)}${hint}`);
      if (key === 'authErrors.codeExpired') {
        setOtp('');
        setVerificationId('');
        setResendIn(0);
        toast(t('login.resendOtpHint'), { icon: 'ℹ️', duration: 4000 });
      }
    } finally {
      setLoading(false);
      verifyingRef.current = false;
      setVerifyingOverlay(null);
    }
  }, [t, verificationId, goHomeAfterAuth]);

  useEffect(() => {
    if (!shouldUseNativePhoneAuth()) return undefined;
    setNativeOtpCallbacks({
      onAutoVerified: async () => {
        const user = auth.currentUser;
        if (!user) return;
        goHomeAfterAuth(user);
      },
      onCodeReceived: (code) => {
        if (auth.currentUser) return;
        const digits = String(code || '').replace(/\D/g, '').slice(0, 6);
        if (digits.length < 6 || !hasPendingOtpSession()) return;
        setOtp(digits);
        completeOtpLogin(digits);
      },
    });
    return () => setNativeOtpCallbacks({ onAutoVerified: null, onCodeReceived: null });
  }, [completeOtpLogin, goHomeAfterAuth]);

  useEffect(() => {
    if (step !== 'otp') return undefined;
    const ac = new AbortController();
    listenForWebOtp((code) => {
      const digits = code.replace(/\D/g, '').slice(0, 6);
      setOtp(digits);
      completeOtpLogin(digits);
    }, ac.signal);
    return () => ac.abort();
  }, [step, completeOtpLogin]);

  const handleSaveName = async () => {
    if (!user?.uid) return;
    if (!isValidName(name)) {
      toast.error(t('login.nameInvalid'));
      return;
    }
    const cleaned = cleanName(name);
    setLoading(true);
    try {
      await updateUserProfile(user.uid, { name: cleaned, profileComplete: true });
      rememberLocalName(cleaned);
      await updateProfile(user, { displayName: cleaned }).catch(() => {});
      clearAwaitingName();
      // Optimistic fallback so a flaky refresh can't bounce back to this screen.
      await refreshUserProfile(user.uid, { name: cleaned, profileComplete: true });
      navigate('/', { replace: true });
    } catch {
      toast.error(t('authErrors.default'));
    } finally { setLoading(false); }
  };

  return (
    <div className="login-screen">
      {verifyingOverlay && (
        <div className="login-loading-overlay" role="status" aria-live="polite">
          <div className="login-loading-card">
            <div className="login-loading-spinner" />
            <h3 className="login-loading-title">{verifyingOverlay.title}</h3>
            {verifyingOverlay.subtitle && (
              <p className="login-loading-sub">{verifyingOverlay.subtitle}</p>
            )}
          </div>
        </div>
      )}
      <div className="login-hero-band">
        <div className="login-lang-toggle"><LangToggle /></div>
        <BrandHero className="brand-hero--login" />
        <div className="login-hero-badge">
          <span>{t('login.subtitle', { city: APP.city }) || `Book autos in ${APP.city}`}</span>
        </div>
      </div>

      <div className="login-form-wrap">
        <div className="login-glass-card">
          {step === 'phone' && (
            <>
              <div className="login-card-header">
                <label className="login-field-label">{t('login.phoneLabel') || 'Mobile number'}</label>
              </div>

              <div className="luxury-phone-input">
                <div className="luxury-phone-prefix">
                  <span className="flag-icon" aria-hidden>🇮🇳</span>
                  <span>+91</span>
                </div>
                <div className="luxury-phone-divider" />
                <input
                  type="tel"
                  className="luxury-phone-field"
                  inputMode="numeric"
                  maxLength={10}
                  placeholder={t('login.phonePlaceholder') || '10-digit mobile number'}
                  value={phone}
                  onChange={(e) => setPhone(e.target.value.replace(/\D/g, '').slice(0, 10))}
                  autoComplete="tel-national"
                />
              </div>

              <button
                type="button"
                className="btn btn-primary luxury-login-btn"
                disabled={loading || phone.length < 10}
                onClick={handleSendOtp}
              >
                {sendingOtp ? (
                  <span className="login-btn-active-content">
                    <span className="login-btn-spinner" aria-hidden="true" />
                    <span>{t('login.sendingOtp') || 'Sending OTP…'}</span>
                  </span>
                ) : loading ? '…' : (t('login.sendOtp') || 'Send OTP')}
              </button>

              <p className="login-legal-consent">
                {t('login.termsPrefix')}{' '}
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => navigate('/legal/terms')}
                >
                  {t('login.termsLink')}
                </button>{' '}
                {t('login.termsAnd')}{' '}
                <button
                  type="button"
                  className="legal-link"
                  onClick={() => navigate('/legal/privacy')}
                >
                  {t('login.privacyLink')}
                </button>
                .
              </p>
            </>
          )}

        {step === 'otp' && (
          <>
            <p className="login-otp-phone text-center">+91 {phone}</p>
            <p className="text-muted text-center">{t('login.otpSent')}</p>
            <p className="login-otp-hint text-muted text-center">{t('login.otpValidHint')}</p>
            <LoginOtpInput
              value={otp}
              disabled={loading}
              onChange={setOtp}
              onComplete={completeOtpLogin}
            />
            <button type="button" className="btn btn-primary" disabled={loading || otp.length < 6} onClick={() => completeOtpLogin(otp)}>
              {t('login.verifyOtp')}
            </button>
            <button type="button" className="btn btn-ghost" disabled={loading || resendIn > 0} onClick={handleSendOtp}>
              {resendIn > 0 ? t('login.resendWait', { s: resendIn }) : t('login.resend')}
            </button>
            <button
              type="button"
              className="btn btn-ghost"
              onClick={() => { setStep('phone'); setOtp(''); }}
            >
              {t('login.changeNumber')}
            </button>
          </>
        )}

        {step === 'name' && (
          <>
            <label className="text-muted field-label">{t('login.nameLabel')}</label>
            <input
              type="text"
              placeholder={t('login.namePlaceholder')}
              value={name}
              onChange={(e) => setName(e.target.value.replace(/[^\p{L}\p{M}\s.'-]/gu, '').slice(0, 40))}
              autoFocus
              autoComplete="name"
            />
            <button type="button" className="btn btn-primary" disabled={loading || !isValidName(name)} onClick={handleSaveName}>
              {t('login.saveName')}
            </button>
          </>
        )}
        </div>

        {(sendingOtp || step === 'otp') && (
          <div className="login-trivia-standalone">
            <PilerTriviaCard />
          </div>
        )}
      </div>
    </div>
  );
}
